import { useCallback, useEffect, useState } from "react"
import { FileSearch, RefreshCw, Ruler } from "lucide-react"
import type { AppShell } from "@/App"
import {
  acceptDatasheets,
  errorMessage,
  estimateProduct,
  listProducts,
  patchProduct,
  refreshProducts,
  requestDimsEstimates,
  requoteAll,
  uploadFile,
  type CatalogLogistics,
  type ProductItem,
  type ProductRow,
  type ProductStats,
} from "@/lib/api"
import { kg, m3, n } from "@/lib/format"
import { AsyncButton, ErrorBox, Field, FileButton, inputCls, Loading, Note, Page, Panel, Tag, Td, Th } from "@/components/kit"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"

/** Ordem: o que mais precisa de atenção primeiro. */
const RANK: Record<string, number> = { unknown: 0, category_avg: 1, catalog: 2, datasheet: 3, validated: 4 }

/** De onde o Koortex tirou o valor (mestre de logística do host). */
const CATALOG_SOURCE: Record<string, string> = {
  manual: "validado no Koortex",
  order_line: "linha da encomenda",
  variant: "variante da loja",
  product: "produto do CRM",
  datasheet: "ficha técnica",
  text_rules: "texto do produto",
  similar: "produtos parecidos",
  estimate: "estimativa IA",
  density: "densidade da categoria",
}
const pct = (v: unknown) => (n(v) !== null ? ` · ${Math.round(n(v)! * 100)} %` : "")

/** Peso e medidas que vêm do catálogo do Koortex, com origem e confiança. */
function CatalogProof({ catalog }: { catalog: CatalogLogistics | null }) {
  if (!catalog) return null
  const estimated = catalog.dims_source && ["text_rules", "similar", "estimate", "density"].includes(catalog.dims_source)
  return (
    <div className="flex flex-wrap gap-1">
      {catalog.weight_source && <Tag tone="info">Peso: {CATALOG_SOURCE[catalog.weight_source] ?? catalog.weight_source}{pct(catalog.weight_confidence)}</Tag>}
      {catalog.dims_source && (
        <Tag tone={estimated ? ((n(catalog.dims_confidence) ?? 0) >= 0.5 ? "warn" : "bad") : "info"}>
          {estimated && "≈ "}Medidas: {CATALOG_SOURCE[catalog.dims_source] ?? catalog.dims_source}{pct(catalog.dims_confidence)}
        </Tag>
      )}
      {catalog.estimate_status === "queued" && <Tag tone="info">medidas a estimar</Tag>}
      {catalog.estimate_status === "failed" && <Tag tone="bad">estimativa falhou</Tag>}
    </div>
  )
}

function Proof({ row, catalog }: { row: ProductRow | null; catalog: CatalogLogistics | null }) {
  if (!row || row.source === "unknown") {
    const ev = row?.evidence
    return (
      <div>
        <Tag tone="bad">{ev?.status === "pending" ? "ficha na fila" : "sem prova"}</Tag>
        {(ev?.rejected || ev?.reason) && <div className="mt-1 text-[11.5px] text-muted-foreground">{ev.rejected ?? ev.reason}</div>}
        <div className="mt-1"><CatalogProof catalog={catalog} /></div>
      </div>
    )
  }
  if (row.source === "catalog") {
    return <div className="max-w-[46ch]"><CatalogProof catalog={catalog} /></div>
  }
  const ev = row.evidence ?? {}
  const tags = ev.tags ?? []
  return (
    <div className="max-w-[46ch]">
      <div className="flex flex-wrap gap-1">
        {row.source === "validated" && <Tag tone="ok">Validado{ev.validated_from ? ` (de ${ev.validated_from === "datasheet" ? "ficha" : ev.validated_from === "category_avg" ? "média" : ev.validated_from})` : ""}</Tag>}
        {row.source === "datasheet" && (
          <>
            <Tag tone="ai">IA</Tag>
            <Tag tone={row.dims_basis === "packaged" ? "ok" : "warn"}>
              {row.dims_basis === "packaged" ? "Ficha · medidas da embalagem" : row.dims_basis === "product" ? "Ficha · medidas do aparelho (+12 %)" : "Ficha · sem medidas"}
            </Tag>
          </>
        )}
        {row.source === "category_avg" && <Tag tone="warn">Média da categoria{ev.category_avg ? ` (${ev.category_avg.n})` : ""}</Tag>}
        {tags.filter((t) => !t.startsWith("medidas do aparelho")).map((t) => <Tag key={t} tone="warn">{t}</Tag>)}
        {ev.status === "pending" && <Tag tone="info">nova ficha na fila</Tag>}
      </div>
      {ev.catalog && row.source !== "validated" && <div className="mt-1"><CatalogProof catalog={catalog} /></div>}
      <div className="mt-1 text-[11.5px] text-muted-foreground">
        {ev.model_reference && <>ref. {ev.model_reference}{n(row.confidence) !== null ? ` · confiança ${Math.round(n(row.confidence)! * 100)} %` : ""} </>}
        {ev.datasheet_url && <a className="text-accent underline" href={ev.datasheet_url} target="_blank" rel="noreferrer">ficha</a>}
        {(ev.quotes ?? []).slice(0, 2).map((q, i) => <div key={i}>«{q.quote}»</div>)}
      </div>
    </div>
  )
}

export function Products({ shell }: { shell: AppShell }) {
  const [data, setData] = useState<{ items: ProductItem[]; stats: ProductStats } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [estimating, setEstimating] = useState<ProductItem | null>(null)
  const [filling, setFilling] = useState<ProductItem | null>(null)
  const [busyMsg, setBusyMsg] = useState<string | null>(null)
  const canEdit = shell.role >= 5

  const load = useCallback(async () => {
    try {
      setData(await listProducts(shell))
    } catch (err) {
      setError(errorMessage(err))
    }
  }, [shell])
  useEffect(() => void load(), [load])

  /** Mudou a carga de algum SKU: as encomendas em aberto recalculam. */
  async function afterCargoChange() {
    await load()
    try {
      await requoteAll(shell, false, (d, t) => setBusyMsg(`A recalcular encomendas ${d}/${t}…`))
    } catch (err) {
      shell.toast("error", "Recalcular falhou", errorMessage(err))
    } finally {
      setBusyMsg(null)
    }
  }

  if (error) return <Page><ErrorBox error={error} /></Page>
  if (!data) return <Loading />
  const { items, stats } = data
  const sorted = [...items].sort((a, b) => (RANK[a.row?.source ?? "unknown"] - RANK[b.row?.source ?? "unknown"]) || b.lines - a.lines)
  const pending = items.filter((i) => i.row?.evidence?.status === "pending").length

  return (
    <Page>
      <Panel
        title="Peso e volume dos produtos vendidos"
        subtitle="Servem para cotar as transportadoras e para encher os veículos. Só aparecem os SKUs das encomendas, por frequência. Quem valida vê de onde vem cada valor."
        actions={canEdit && (
          <>
            {stats.missing_dims > 0 && (
              <AsyncButton size="sm" variant="outline" onRun={async () => {
                try {
                  const r = await requestDimsEstimates(shell)
                  shell.toast("info", "Medidas pedidas", r.queued ? `${r.queued} SKU(s) na fila de estimativa — volta daqui a pouco` : "Nada a estimar (já estimados ou sem dados)")
                  await load()
                } catch (e) {
                  shell.toast("error", "Não foi possível pedir as medidas", errorMessage(e))
                }
              }}>
                <Ruler className="h-3.5 w-3.5" /> Estimar medidas em falta ({stats.missing_dims})
              </AsyncButton>
            )}
            {pending > 0 && (
              <AsyncButton size="sm" variant="outline" onRun={async () => {
                const r = await refreshProducts(shell)
                shell.toast("info", "Fichas atualizadas", `${r.items.filter((i) => i.status !== "pending").length} de ${r.items.length} prontas`)
                await afterCargoChange()
              }}>
                <RefreshCw className="h-3.5 w-3.5" /> Atualizar fichas na fila ({pending})
              </AsyncButton>
            )}
            <AsyncButton size="sm" disabled={!stats.batch_acceptable} onRun={async () => {
              try {
                const r = await acceptDatasheets(shell)
                shell.toast("success", "Validadas em lote", `${r.accepted.length} com ficha e medidas da embalagem`)
                await afterCargoChange()
              } catch (e) {
                shell.toast("error", "Não foi possível validar", errorMessage(e))
              }
            }}>
              Aceitar as que têm ficha com medidas da embalagem ({stats.batch_acceptable})
            </AsyncButton>
          </>
        )}
      >
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-muted-foreground">
          <span><b className="font-mono text-foreground">{stats.skus}</b> SKUs em encomendas</span>
          <span className={cn(stats.validated_lines_pct >= 90 ? "text-green-700" : "text-amber-700")}>
            <b className="font-mono">{stats.validated_lines_pct.toLocaleString("pt-PT")} %</b> das linhas com carga validada (meta 90 %)
          </span>
          <span><b className="font-mono text-foreground">{stats.validated}</b> validados</span>
          <span><b className="font-mono text-foreground">{stats.datasheet}</b> com ficha</span>
          <span><b className="font-mono text-foreground">{stats.catalog}</b> do catálogo</span>
          <span><b className="font-mono text-foreground">{stats.category_avg}</b> média da categoria</span>
          {stats.estimating > 0 && <span className="text-blue-700"><b className="font-mono">{stats.estimating}</b> com medidas a estimar</span>}
          <span className={cn(stats.unknown && "text-red-700")}><b className="font-mono">{stats.unknown}</b> sem prova</span>
        </div>
        {busyMsg && <div className="mt-2 text-sm text-blue-700">{busyMsg}</div>}
        <div className="mt-3 overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <Th>Produto</Th>
                <Th className="text-right">Em encomendas</Th>
                <Th className="text-right">Peso</Th>
                <Th>Medidas (cm)</Th>
                <Th className="text-right">Volume</Th>
                <Th>Classe</Th>
                <Th>Prova</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {sorted.map((it) => {
                const r = it.row
                const known = r && r.source !== "unknown"
                return (
                  <tr key={it.sku}>
                    <Td>
                      <div className="font-medium">{it.name ?? "—"}</div>
                      <div className="font-mono text-[11.5px] text-muted-foreground">{it.sku}</div>
                    </Td>
                    <Td className="text-right font-mono text-xs">
                      {it.qty} <span className="text-muted-foreground">({it.orders} enc.)</span>
                      {it.blocked_orders > 0 && <div className="text-[11px] text-red-700">bloqueia {it.blocked_orders}</div>}
                    </Td>
                    <Td className="text-right font-mono text-xs">{known ? kg(r.weight_kg) : "—"}</Td>
                    <Td className="font-mono text-xs">{known && n(r.length_cm) ? `${n(r.length_cm)} × ${n(r.width_cm)} × ${n(r.height_cm)}` : "—"}</Td>
                    <Td className="text-right font-mono text-xs">{known ? m3(r.volume_m3) : "—"}</Td>
                    <Td>
                      {known && r.load_class ? <Tag>{r.load_class}</Tag> : "—"}
                      {known && r.needs_tail_lift && <span className="ml-1"><Tag tone="info">plataforma</Tag></span>}
                    </Td>
                    <Td><Proof row={r} catalog={it.catalog} /></Td>
                    <Td className="whitespace-nowrap text-right">
                      {canEdit && (
                        <div className="flex justify-end gap-1">
                          {known && r.source !== "validated" && (
                            <AsyncButton size="sm" variant="outline" className="h-7" onRun={async () => {
                              try {
                                await patchProduct(shell, { sku: it.sku, validate: true })
                                await afterCargoChange()
                              } catch (e) {
                                shell.toast("error", "Não foi possível validar", errorMessage(e))
                              }
                            }}>Validar</AsyncButton>
                          )}
                          <Button size="sm" variant="ghost" className="h-7" onClick={() => setEstimating(it)}><FileSearch className="h-3.5 w-3.5" /> Ficha</Button>
                          <Button size="sm" variant="ghost" className="h-7" onClick={() => setFilling(it)}>Preencher</Button>
                        </div>
                      )}
                    </Td>
                  </tr>
                )
              })}
              {sorted.length === 0 && (
                <tr><Td colSpan={8} className="py-8 text-center text-muted-foreground">Ainda não há encomendas importadas.</Td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Note>
          A ordem é: validado › ficha técnica (referência exata, medidas da embalagem) › catálogo do Koortex (peso da loja ou da linha da encomenda; medidas da loja ou estimadas, marcadas com ≈ e a confiança) › média da categoria › desconhecido. Medidas do aparelho levam +12 % por dimensão e ficam assinaladas. «Estimar medidas» pede ao Koortex as que faltam: primeiro o texto do produto e os parecidos, depois a IA com o Jev a calibrar. O que validares aqui fica no catálogo do Koortex para todas as apps.
        </Note>
      </Panel>
      {estimating && <EstimateDialog shell={shell} item={estimating} onClose={() => setEstimating(null)} onDone={afterCargoChange} />}
      {filling && <FillDialog shell={shell} item={filling} onClose={() => setFilling(null)} onDone={afterCargoChange} />}
    </Page>
  )
}

function EstimateDialog({ shell, item, onClose, onDone }: { shell: AppShell; item: ProductItem; onClose: () => void; onDone: () => Promise<void> }) {
  const [url, setUrl] = useState(item.row?.evidence?.datasheet_url ?? "")
  const [msg, setMsg] = useState<string | null>(null)
  async function run(body: { datasheet_url?: string; file_key?: string }) {
    try {
      setMsg("A ler a ficha…")
      const r = await estimateProduct(shell, { sku: item.sku, ...body })
      setMsg(r.message)
      await onDone()
    } catch (e) {
      setMsg(errorMessage(e))
    }
  }
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Ficha técnica · <span className="font-mono">{item.sku}</span></DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">
          PDF do fabricante com a referência exata. Sem URL nem ficheiro, usa a ficha do produto no CRM (se houver) ou a média da categoria.
        </p>
        <Field label="URL do PDF"><input className={inputCls} placeholder="https://…/ficha.pdf" value={url} onChange={(e) => setUrl(e.target.value)} /></Field>
        <div className="flex flex-wrap gap-2">
          <AsyncButton size="sm" onRun={() => run(url.trim() ? { datasheet_url: url.trim() } : {})}>{url.trim() ? "Ler este URL" : "Estimar"}</AsyncButton>
          <FileButton size="sm" variant="outline" accept="application/pdf" onFile={async (f) => run({ file_key: await uploadFile(shell, f) })}>Enviar PDF</FileButton>
        </div>
        {msg && <div className="rounded-md border bg-muted/40 p-2 text-sm">{msg}</div>}
      </DialogContent>
    </Dialog>
  )
}

function FillDialog({ shell, item, onClose, onDone }: { shell: AppShell; item: ProductItem; onClose: () => void; onDone: () => Promise<void> }) {
  const r = item.row
  const [f, setF] = useState({
    weight_kg: r && r.source !== "unknown" ? String(n(r.weight_kg) ?? "") : "",
    length_cm: r && n(r.length_cm) ? String(n(r.length_cm)) : "",
    width_cm: r && n(r.width_cm) ? String(n(r.width_cm)) : "",
    height_cm: r && n(r.height_cm) ? String(n(r.height_cm)) : "",
    load_class: "",
  })
  const bind = (k: keyof typeof f) => ({ value: f[k], onChange: (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value }) })
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Preencher · <span className="font-mono">{item.sku}</span></DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Peso e medidas <b>com embalagem</b>, de um volume. Fica validado por ti.</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Peso (kg)"><input className={inputCls} {...bind("weight_kg")} /></Field>
          <Field label="Classe" hint="vazio = calcula pelo peso e volume">
            <select className={inputCls} {...bind("load_class")}>
              <option value="">automática</option>
              {["XS", "S", "M", "L", "XL"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Comprimento (cm)"><input className={inputCls} {...bind("length_cm")} /></Field>
          <Field label="Largura (cm)"><input className={inputCls} {...bind("width_cm")} /></Field>
          <Field label="Altura (cm)"><input className={inputCls} {...bind("height_cm")} /></Field>
        </div>
        <div className="flex justify-end">
          <AsyncButton size="sm" onRun={async () => {
            try {
              const num = (v: string) => Number(v.replace(",", "."))
              await patchProduct(shell, {
                sku: item.sku,
                name: item.name,
                weight_kg: num(f.weight_kg),
                length_cm: num(f.length_cm),
                width_cm: num(f.width_cm),
                height_cm: num(f.height_cm),
                ...(f.load_class ? { load_class: f.load_class } : {}),
              })
              await onDone()
              onClose()
            } catch (e) {
              shell.toast("error", "Não foi possível gravar", errorMessage(e))
            }
          }}>Gravar e validar</AsyncButton>
        </div>
      </DialogContent>
    </Dialog>
  )
}
